interface ElementDetail {
  options?: unknown[] | string | null;
  [key: string]: unknown;
}

interface ElementFormData {
  details: ElementDetail[];
  [key: string]: unknown;
}

interface PrepareElementPayloadParams {
  userId: string | undefined;
  clientId: string | number | undefined;
  data?: { id?: string | number } | null;
  formData: ElementFormData;
}

interface PreparedElementPayload {
  userId: string | undefined;
  clientId: string | number | undefined;
  id: string | number | undefined;
  elementData: ElementFormData & { id: string | number | undefined };
}

interface CurrentClient {
  id?: string | number;
  name?: string;
}

interface BreadcrumbItem {
  label: string;
  href: string;
}

export const getCommonRedirectLink = (
  clientId: string | number | undefined,
  year: string | number
): string => `/admin/clients/${clientId}/critical-elements/${year}`;

/**
 * Gets the base path to the list of critical elements by removing the last segment from the current URL
 * @param path Current URL
 * @returns Base path to the list of critical elements
 */
export const getPathToElements = (path: string): string => {
  const parts = path.split('/');
  const clientIdIndex = parts.indexOf('clients') + 1;
  const criticalElementsIndex = parts.indexOf('critical-elements');
  const clientId = parts[clientIdIndex];

  if (
    criticalElementsIndex !== -1 &&
    parts.length > criticalElementsIndex + 1
  ) {
    const year = parts[criticalElementsIndex + 1];

    if (!Number.isNaN(Number.parseInt(year, 10))) {
      return getCommonRedirectLink(clientId, year);
    }
  }

  return getCommonRedirectLink(clientId, '');
};

/**
 * Gets the identifier from the last segment of the URL
 * @param path Current URL
 * @returns Element ID or empty string
 */
export const getLastPathSegment = (path: string): string => {
  const pathSegments = path.split('/').filter((segment) => segment !== '');

  return pathSegments.length ? pathSegments[pathSegments.length - 1] : '';
};

/**
 * Prepares critical element data for sending to the server
 * @param params Parameters for data preparation
 * @returns Prepared data for sending
 */
export const prepareElementPayload = ({
  userId,
  clientId,
  data,
  formData,
}: PrepareElementPayloadParams): PreparedElementPayload => {
  const id = data?.id || undefined;

  return {
    userId,
    clientId,
    id,
    elementData: {
      ...formData,
      id,
      details: formData.details.map((item) => ({
        ...item,
        options: Array.isArray(item.options)
          ? item.options
          : item.options
          ? [item.options]
          : [],
      })),
    },
  };
};

export const getBreadcrumbsForCriticalElement = (
  currentClient: CurrentClient | null | undefined,
  year: string | number
): BreadcrumbItem[] => [
  { label: 'Clients', href: '/admin/clients' },
  {
    label: currentClient?.name || '',
    href: `/admin/clients/${currentClient?.id}`,
  },
  {
    label: 'Critical Elements',
    href: getCommonRedirectLink(currentClient?.id, year),
  },
];
